import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Trash2,
  Hash,
  Circle,
  Square,
  RectangleHorizontal,
  Layers,
  MapPin,
  Info,
  CheckSquare,
  MousePointer2,
  Ban,
  Lock,
  Sparkles,
  CircleCheck,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EVENTO_WIZARD, EVENTO_PLANO } from "@/constants/testIds";
import {
  ESTADO_MESA,
  ESTADO_MESA_LABEL,
  TIPO_MESA,
  TIPO_MESA_LABEL,
  CAPACIDADES_MESA,
  getElementosFijosPorSalon,
  nuevoIdMesa,
  siguientePosicionMesa,
  computarCapacidad,
  salonesUtilizados,
} from "@/data/adminEventosStore";

// Distancia mínima (en % del canvas) entre centros de mesa para
// considerar que NO colisionan. 9% cubre visualmente el diámetro de
// una mesa redonda (8%) + un pequeño gap.
const COLLISION_THRESHOLD = 9;

/** ¿Colisiona la mesa `id` colocada en (x,y) con alguna otra mesa? */
const chocaCon = (id, x, y, mesas) => {
  for (const m of mesas) {
    if (m.id === id) continue;
    const dx = (m.x || 0) - x;
    const dy = (m.y || 0) - y;
    const dist = Math.hypot(dx, dy);
    if (dist < COLLISION_THRESHOLD) return true;
  }
  return false;
};

/**
 * Paso 3 · Plano y Mesas (Tanda 2 · Auditoría 055-079).
 *
 * Novedades:
 *  - Capacidad: 4/6/8/10/12 o personalizada (entero > 0).
 *  - Botón "Eliminar" siempre visible en el panel + acciones masivas.
 *  - Selección múltiple: acciones rápidas (Disponible/Reservar/Protocolo/
 *    Bloquear/Cambiar capacidad/Eliminar).
 *  - Drag & drop con detección de colisión: no permite superposición.
 *  - Edición en tiempo real: cada onChange aplica al plano al instante;
 *    NO existe botón "Aplicar cambios".
 */
export default function PasoPlanoMesas({ valores, onChange, errores, soloDisponible = false }) {
  const salones = useMemo(() => salonesUtilizados(valores), [valores]);
  const salonPrincipal = salones[0] || "";
  const mesas = Array.isArray(valores.mesas) ? valores.mesas : [];
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [duplicadoMsg, setDuplicadoMsg] = useState("");
  const [capacidadCustomOpen, setCapacidadCustomOpen] = useState(false);
  const [capacidadCustomValor, setCapacidadCustomValor] = useState("");

  const elementosFijos = useMemo(
    () => getElementosFijosPorSalon(salonPrincipal),
    [salonPrincipal],
  );
  const capacidadTotal = useMemo(() => computarCapacidad(mesas), [mesas]);

  useEffect(() => {
    if (seleccionadas.length > 0) {
      const existentes = new Set(mesas.map((m) => m.id));
      const filtradas = seleccionadas.filter((id) => existentes.has(id));
      if (filtradas.length !== seleccionadas.length)
        setSeleccionadas(filtradas);
    }
  }, [mesas, seleccionadas]);

  const setEvento = (patch) => {
    Object.entries(patch).forEach(([k, v]) => onChange(k, v));
  };

  const [form, setForm] = useState({
    tipo: TIPO_MESA.REDONDA,
    capacidad: 8,
  });
  const [formCapacidadCustom, setFormCapacidadCustom] = useState("");
  const [formCapacidadModo, setFormCapacidadModo] = useState("std"); // std|custom

  const agregarMesa = () => {
    const capNum = Number(
      formCapacidadModo === "custom" ? formCapacidadCustom : form.capacidad,
    );
    if (!Number.isInteger(capNum) || capNum <= 0) {
      toast.error("La capacidad debe ser un entero mayor a cero.");
      return;
    }
    const existentes = new Set(
      mesas
        .filter((m) => (m.salon || salonPrincipal) === salonPrincipal)
        .map((m) => m.numero),
    );
    let numero = 1;
    while (existentes.has(numero)) numero += 1;

    // Buscar una posición inicial libre de colisiones.
    let { x, y } = siguientePosicionMesa(mesas);
    let intentos = 0;
    while (chocaCon(null, x, y, mesas) && intentos < 40) {
      x = Math.min(96, Math.max(4, x + COLLISION_THRESHOLD));
      if (x >= 96) {
        x = 8;
        y = Math.min(90, y + COLLISION_THRESHOLD);
      }
      intentos += 1;
    }

    const nueva = {
      id: nuevoIdMesa(),
      numero,
      tipo: form.tipo,
      capacidad: capNum,
      estado: ESTADO_MESA.DISPONIBLE,
      observaciones: "",
      salon: salonPrincipal,
      x,
      y,
      rotacion: 0,
    };
    const nuevas = [...mesas, nueva];
    setEvento({ mesas: nuevas, capacidad: computarCapacidad(nuevas) });
    setSeleccionadas([nueva.id]);
    toast.success(`Mesa ${numero} agregada.`);
  };

  const aplicarAMesa = (id, patch) => {
    if (patch.numero !== undefined) {
      const nuevoNum = Number(patch.numero);
      if (!Number.isFinite(nuevoNum) || nuevoNum < 1) return false;
      const target = mesas.find((m) => m.id === id);
      const salonTarget = target?.salon || salonPrincipal;
      const dup = mesas.find(
        (m) =>
          m.id !== id &&
          (m.salon || salonPrincipal) === salonTarget &&
          Number(m.numero) === nuevoNum,
      );
      if (dup) {
        setDuplicadoMsg(`La mesa ${nuevoNum} ya existe. Ingresa otro número.`);
        toast.error(`La mesa ${nuevoNum} ya existe. Ingresa otro número.`);
        return false;
      }
    }
    setDuplicadoMsg("");
    const nuevas = mesas.map((m) => (m.id === id ? { ...m, ...patch } : m));
    setEvento({ mesas: nuevas, capacidad: computarCapacidad(nuevas) });
    return true;
  };

  const aplicarSeleccion = (patch) => {
    if (seleccionadas.length === 0) return;
    const nuevas = mesas.map((m) =>
      seleccionadas.includes(m.id) ? { ...m, ...patch } : m,
    );
    setEvento({ mesas: nuevas, capacidad: computarCapacidad(nuevas) });
  };

  const eliminarSeleccion = () => {
    if (seleccionadas.length === 0) return;
    const cuantas = seleccionadas.length;
    const nuevas = mesas.filter((m) => !seleccionadas.includes(m.id));
    setEvento({ mesas: nuevas, capacidad: computarCapacidad(nuevas) });
    setSeleccionadas([]);
    toast.success(
      `${cuantas} ${cuantas === 1 ? "mesa eliminada" : "mesas eliminadas"}.`,
    );
  };

  const renumerarDesde = () => {
    if (seleccionadas.length !== 1) {
      toast.error("Selecciona exactamente una mesa que será Mesa 1.");
      return;
    }
    const primeraId = seleccionadas[0];
    const primera = mesas.find((m) => m.id === primeraId);
    if (!primera) return;
    const resto = mesas
      .filter((m) => m.id !== primeraId)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((m, i) => ({ ...m, numero: i + 2 }));
    const nuevas = [{ ...primera, numero: 1 }, ...resto].sort(
      (a, b) => a.numero - b.numero,
    );
    setEvento({ mesas: nuevas });
    toast.success("Mesas renumeradas.");
  };

  const clickMesa = (mesa, event) => {
    const multi = event.ctrlKey || event.metaKey || event.shiftKey;
    if (multi) {
      setSeleccionadas((prev) =>
        prev.includes(mesa.id)
          ? prev.filter((id) => id !== mesa.id)
          : [...prev, mesa.id],
      );
    } else {
      setSeleccionadas([mesa.id]);
    }
  };

  // Drag & drop con detección de colisión.
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const onMesaPointerDown = (mesa, event) => {
    event.preventDefault();
    event.stopPropagation();
    clickMesa(mesa, event);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((event.clientX - rect.left) / rect.width) * 100;
    const py = ((event.clientY - rect.top) / rect.height) * 100;
    dragRef.current = {
      id: mesa.id,
      offsetX: px - mesa.x,
      offsetY: py - mesa.y,
      lastValidX: mesa.x,
      lastValidY: mesa.y,
    };
    if (event.target && event.target.setPointerCapture) {
      try {
        event.target.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((event.clientX - rect.left) / rect.width) * 100;
    const py = ((event.clientY - rect.top) / rect.height) * 100;
    const { id, offsetX, offsetY } = dragRef.current;
    const margen = 4;
    const nxCandidate = Math.max(margen, Math.min(100 - margen, px - offsetX));
    const nyCandidate = Math.max(margen, Math.min(100 - margen, py - offsetY));
    const nxRounded = Math.round(nxCandidate * 10) / 10;
    const nyRounded = Math.round(nyCandidate * 10) / 10;

    // Colisión: si choca, retenemos la última posición válida y NO
    // actualizamos. El usuario "empuja" contra el borde invisible.
    if (chocaCon(id, nxRounded, nyRounded, mesas)) return;

    dragRef.current.lastValidX = nxRounded;
    dragRef.current.lastValidY = nyRounded;
    const nuevas = mesas.map((m) =>
      m.id === id ? { ...m, x: nxRounded, y: nyRounded } : m,
    );
    setEvento({ mesas: nuevas });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  // --- Acciones masivas quick chips ---
  const aplicarEstadoMasivo = (estado) => {
    if (seleccionadas.length === 0) return;
    aplicarSeleccion({ estado });
    toast.success(
      `${seleccionadas.length} ${seleccionadas.length === 1 ? "mesa actualizada" : "mesas actualizadas"} a ${ESTADO_MESA_LABEL[estado]}.`,
    );
  };

  const aplicarCapacidadMasiva = (cap) => {
    const n = Number(cap);
    if (!Number.isInteger(n) || n <= 0) {
      toast.error("La capacidad debe ser un entero mayor a cero.");
      return;
    }
    aplicarSeleccion({ capacidad: n });
    toast.success(`Capacidad actualizada a ${n} personas.`);
    setCapacidadCustomOpen(false);
    setCapacidadCustomValor("");
  };

  return (
    <div
      data-testid={EVENTO_WIZARD.stepContent(3)}
      className="flex flex-col gap-6"
    >
      <header>
        <span className="overline text-accent">Paso 3</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Plano y mesas
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Los cambios se aplican en tiempo real sobre el plano. Arrastra las
          mesas para reubicarlas — no se permite superposición.
        </p>
      </header>

      <div className="rounded-2xl border hairline bg-card px-5 md:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <MapPin className="w-4 h-4 text-accent" strokeWidth={1.7} />
          <div>
            <p className="overline text-muted-foreground">Salón principal</p>
            <p
              data-testid={EVENTO_PLANO.salonActivo}
              className="font-medium text-sm"
            >
              {salonPrincipal || "No definido en Paso 1"}
              {salones.length > 1 && (
                <span className="text-muted-foreground text-xs ml-2">
                  +{salones.length - 1} salón(es)
                </span>
              )}
            </p>
          </div>
        </div>
        <div
          data-testid={EVENTO_PLANO.capacidadTotal}
          className="text-right"
        >
          <p className="overline text-muted-foreground">Capacidad total</p>
          <p className="font-heading text-2xl leading-none">
            {capacidadTotal}{" "}
            <span className="text-sm text-muted-foreground">personas</span>
          </p>
        </div>
      </div>

      {errores.mesas && (
        <p className="text-sm text-destructive -mt-2">{errores.mesas}</p>
      )}
      {duplicadoMsg && (
        <p
          data-testid={EVENTO_PLANO.duplicadoError}
          className="text-sm text-destructive -mt-2"
        >
          {duplicadoMsg}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <Plano
          canvasRef={canvasRef}
          mesas={mesas}
          elementosFijos={elementosFijos}
          seleccionadas={seleccionadas}
          onMesaPointerDown={onMesaPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onFondoClick={() => setSeleccionadas([])}
        />

        <aside className="flex flex-col gap-5">
          <FormAgregar
            form={form}
            setForm={setForm}
            formCapacidadModo={formCapacidadModo}
            setFormCapacidadModo={setFormCapacidadModo}
            formCapacidadCustom={formCapacidadCustom}
            setFormCapacidadCustom={setFormCapacidadCustom}
            onAgregar={agregarMesa}
          />

          <ToolbarSeleccion
            total={mesas.length}
            seleccionadas={seleccionadas}
            onSeleccionarTodas={() => setSeleccionadas(mesas.map((m) => m.id))}
            onDeseleccionar={() => setSeleccionadas([])}
          />

          {seleccionadas.length > 1 && (
            <AccionesMasivas
              cantidad={seleccionadas.length}
              onEstado={aplicarEstadoMasivo}
              soloDisponible={soloDisponible}
              onCapacidad={aplicarCapacidadMasiva}
              capacidadCustomOpen={capacidadCustomOpen}
              setCapacidadCustomOpen={setCapacidadCustomOpen}
              capacidadCustomValor={capacidadCustomValor}
              setCapacidadCustomValor={setCapacidadCustomValor}
              onEliminar={eliminarSeleccion}
            />
          )}

          <div className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent ring-1 ring-primary/20 dark:ring-accent/30 grid place-items-center">
                <Hash className="w-4 h-4" strokeWidth={1.7} />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading text-base leading-tight">
                  Renumerar
                </h3>
                <p className="text-xs text-muted-foreground">
                  Elige la mesa que será la Mesa 1.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid={EVENTO_PLANO.btnRenumerar}
              onClick={renumerarDesde}
              disabled={seleccionadas.length !== 1}
              className="rounded-xl w-full"
            >
              Renumerar desde la mesa seleccionada
            </Button>
          </div>

          {seleccionadas.length > 0 ? (
            <PanelConfiguracionMesas
              mesas={mesas.filter((m) => seleccionadas.includes(m.id))}
              onAplicarUno={aplicarAMesa}
              onAplicarSeleccion={aplicarSeleccion}
              onEliminar={eliminarSeleccion}
              soloDisponible={soloDisponible}
            />
          ) : (
            <div className="rounded-2xl border hairline bg-muted/30 p-6 text-sm text-muted-foreground flex items-center gap-3">
              <Info className="w-4 h-4 text-accent" strokeWidth={1.7} />
              Selecciona una o varias mesas del plano para editarlas.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* -------- Subcomponentes -------- */

const ESTADO_MESA_TONE = {
  DISPONIBLE: {
    bg: "bg-emerald-500/15",
    ring: "ring-emerald-500/40",
    text: "text-emerald-800 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
  RESERVADA: {
    bg: "bg-amber-500/15",
    ring: "ring-amber-500/40",
    text: "text-amber-800 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  PROTOCOLO: {
    bg: "bg-accent/20",
    ring: "ring-accent/40",
    text: "text-accent",
    dot: "bg-accent",
  },
  BLOQUEADA: {
    bg: "bg-destructive/15",
    ring: "ring-destructive/40",
    text: "text-destructive",
    dot: "bg-destructive",
  },
};

const TIPO_MESA_ICON = {
  REDONDA: Circle,
  CUADRADA: Square,
  RECTANGULAR: RectangleHorizontal,
};

function Plano({
  canvasRef,
  mesas,
  elementosFijos,
  seleccionadas,
  onMesaPointerDown,
  onPointerMove,
  onPointerUp,
  onFondoClick,
}) {
  return (
    <div
      data-testid={EVENTO_PLANO.canvas}
      className="rounded-2xl border hairline bg-card p-4 md:p-6"
    >
      <header className="flex items-center gap-3 mb-4">
        <Layers className="w-4 h-4 text-accent" strokeWidth={1.7} />
        <p className="text-sm font-medium">Plano del salón</p>
        <p className="text-xs text-muted-foreground ml-auto">
          {mesas.length} {mesas.length === 1 ? "mesa" : "mesas"}
          {seleccionadas.length > 0 && ` · ${seleccionadas.length} seleccionadas`}
        </p>
      </header>

      <div
        ref={canvasRef}
        data-testid={EVENTO_PLANO.container}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onClick={(e) => {
          if (e.target === e.currentTarget) onFondoClick();
        }}
        className="relative w-full aspect-[3/2] rounded-xl border border-dashed border-border/70 bg-muted/30 overflow-hidden touch-none select-none"
      >
        {elementosFijos.map((el) => (
          <div
            key={el.id}
            data-testid={EVENTO_PLANO.elementoFijo(el.id)}
            className="absolute rounded-md bg-muted/70 border hairline text-[10px] uppercase tracking-widest text-muted-foreground grid place-items-center px-1 text-center leading-tight pointer-events-none"
            style={{
              left: `${el.x}%`,
              top: `${el.y}%`,
              width: `${el.w}%`,
              height: `${el.h}%`,
            }}
          >
            {el.label}
          </div>
        ))}

        {mesas.map((m) => {
          const tone = ESTADO_MESA_TONE[m.estado] || ESTADO_MESA_TONE.DISPONIBLE;
          const isRect = m.tipo === TIPO_MESA.RECTANGULAR;
          const isSquare = m.tipo === TIPO_MESA.CUADRADA;
          const esDirectiva = /directiva/i.test(m.observaciones || "");
          const esVertical = Number(m.rotacion) === 90;
          const seleccionada = seleccionadas.includes(m.id);
          return (
            <button
              key={m.id}
              type="button"
              data-testid={EVENTO_PLANO.mesa(m.numero)}
              onPointerDown={(e) => onMesaPointerDown(m, e)}
              aria-label={esDirectiva ? `Mesa ${m.numero} · Directiva` : `Mesa ${m.numero}`}
              title={m.observaciones || undefined}
              className={cn(
                "absolute -translate-x-1/2 -translate-y-1/2",
                "grid place-items-center leading-none",
                "text-[11px] font-medium cursor-grab active:cursor-grabbing",
                tone.bg,
                tone.text,
                "ring-2 transition-shadow",
                seleccionada
                  ? "ring-primary shadow-lg z-20"
                  : esDirectiva
                    ? "ring-amber-400 z-10"
                    : `${tone.ring} z-10`,
                esDirectiva
                  ? "w-[15%] h-[6.5%] rounded-[2px] ring-2"
                  : isRect
                    ? esVertical
                      ? "w-[3%] h-[9%] rounded-[2px]"
                      : "w-[11%] h-[5%] rounded-[2px]"
                    : isSquare
                      ? "w-[7%] h-[8%] rounded-[2px]"
                      : "w-[8%] aspect-square rounded-full",
              )}
              style={{ left: `${m.x}%`, top: `${m.y}%`, touchAction: "none" }}
            >
              {esDirectiva ? (
                <span className="flex flex-col items-center leading-none">
                  <span className="inline-flex items-center gap-0.5">
                    <Crown className="w-2.5 h-2.5" /> {m.numero}
                  </span>
                  <span className="text-[7px] uppercase tracking-tight opacity-90">
                    Directiva
                  </span>
                </span>
              ) : (
                m.numero
              )}
            </button>
          );
        })}

        {mesas.length === 0 && (
          <div
            data-testid={EVENTO_PLANO.emptyMesas}
            className="absolute inset-0 grid place-items-center text-center px-8 pointer-events-none"
          >
            <div>
              <p className="font-heading text-lg">Sin mesas todavía</p>
              <p className="text-sm text-muted-foreground mt-1">
                Usa el panel derecho para agregar la primera mesa.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-[11px]">
        {Object.values(ESTADO_MESA).map((e) => (
          <span
            key={e}
            className="inline-flex items-center gap-1.5 text-muted-foreground"
          >
            <span
              className={cn("w-2.5 h-2.5 rounded-full", ESTADO_MESA_TONE[e].dot)}
            />
            {ESTADO_MESA_LABEL[e]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-muted-foreground ml-auto">
          <MousePointer2 className="w-3 h-3" strokeWidth={1.7} />
          Arrastra las mesas · sin superposición
        </span>
      </div>
    </div>
  );
}

function FormAgregar({
  form,
  setForm,
  formCapacidadModo,
  setFormCapacidadModo,
  formCapacidadCustom,
  setFormCapacidadCustom,
  onAgregar,
}) {
  return (
    <div className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center">
          <Plus className="w-4 h-4" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-heading text-base leading-tight">Agregar mesa</h3>
          <p className="text-xs text-muted-foreground">
            Se coloca automáticamente sin encimarse.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Tipo</Label>
          <Select
            value={form.tipo}
            onValueChange={(v) => setForm((s) => ({ ...s, tipo: v }))}
          >
            <SelectTrigger
              data-testid={EVENTO_PLANO.tipoMesa}
              className="bg-background"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIPO_MESA).map(([k, v]) => (
                <SelectItem key={k} value={v}>
                  {TIPO_MESA_LABEL[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Capacidad</Label>
          <Select
            value={
              formCapacidadModo === "custom"
                ? "__custom__"
                : String(form.capacidad)
            }
            onValueChange={(v) => {
              if (v === "__custom__") {
                setFormCapacidadModo("custom");
              } else {
                setFormCapacidadModo("std");
                setForm((s) => ({ ...s, capacidad: Number(v) }));
              }
            }}
          >
            <SelectTrigger
              data-testid={EVENTO_PLANO.capacidadMesa}
              className="bg-background"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAPACIDADES_MESA.map((c) => (
                <SelectItem key={c} value={String(c)}>
                  {c} personas
                </SelectItem>
              ))}
              <SelectItem value="__custom__">Personalizada…</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {formCapacidadModo === "custom" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Capacidad personalizada</Label>
          <Input
            type="number"
            min={1}
            step={1}
            data-testid="plano-form-capacidad-custom"
            value={formCapacidadCustom}
            onChange={(e) => setFormCapacidadCustom(e.target.value)}
            placeholder="Número entero mayor a cero"
            className="bg-background"
          />
        </div>
      )}

      <Button
        type="button"
        data-testid={EVENTO_PLANO.btnAgregar}
        onClick={onAgregar}
        className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 min-h-[44px]"
      >
        <Plus className="w-4 h-4 mr-2" />
        Agregar mesa
      </Button>
    </div>
  );
}

function ToolbarSeleccion({
  total,
  seleccionadas,
  onSeleccionarTodas,
  onDeseleccionar,
}) {
  return (
    <div className="rounded-2xl border hairline bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center">
          <CheckSquare className="w-4 h-4" strokeWidth={1.7} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Selección</p>
          <p
            data-testid={EVENTO_PLANO.seleccionCount}
            className="text-xs text-muted-foreground"
          >
            {seleccionadas.length} de {total}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={EVENTO_PLANO.btnSeleccionarTodas}
          disabled={total === 0}
          onClick={onSeleccionarTodas}
          className="rounded-xl flex-1"
        >
          Todas
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={EVENTO_PLANO.btnDeseleccionar}
          disabled={seleccionadas.length === 0}
          onClick={onDeseleccionar}
          className="rounded-xl flex-1"
        >
          Deseleccionar
        </Button>
      </div>
    </div>
  );
}

/** Acciones rápidas para selección múltiple. */
function AccionesMasivas({
  cantidad,
  onEstado,
  onCapacidad,
  capacidadCustomOpen,
  setCapacidadCustomOpen,
  capacidadCustomValor,
  setCapacidadCustomValor,
  onEliminar,
  soloDisponible = false,
}) {
  return (
    <div className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center">
          <Sparkles className="w-4 h-4" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-heading text-base leading-tight">
            Acciones masivas
          </h3>
          <p className="text-xs text-muted-foreground">
            {cantidad} mesas seleccionadas · el cambio se aplica al instante.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="plano-mass-disponible"
          onClick={() => onEstado(ESTADO_MESA.DISPONIBLE)}
          className="rounded-xl text-emerald-700 dark:text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10"
        >
          <CircleCheck className="w-3.5 h-3.5 mr-1.5" />
          Disponible
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="plano-mass-reservar"
          onClick={() => onEstado(ESTADO_MESA.RESERVADA)}
          className={cn("rounded-xl text-amber-700 dark:text-amber-300 border-amber-500/40 hover:bg-amber-500/10", soloDisponible && "hidden")}
        >
          <Circle className="w-3.5 h-3.5 mr-1.5" />
          Reservar
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="plano-mass-protocolo"
          onClick={() => onEstado(ESTADO_MESA.PROTOCOLO)}
          className={cn("rounded-xl text-accent border-accent/40 hover:bg-accent/10", soloDisponible && "hidden")}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          Protocolo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="plano-mass-bloquear"
          onClick={() => onEstado(ESTADO_MESA.BLOQUEADA)}
          className={cn("rounded-xl text-destructive border-destructive/40 hover:bg-destructive/10", soloDisponible && "hidden")}
        >
          <Lock className="w-3.5 h-3.5 mr-1.5" />
          Bloquear
        </Button>
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t hairline">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider">
          Cambiar capacidad
        </Label>
        <div className="grid grid-cols-5 gap-1.5">
          {CAPACIDADES_MESA.map((c) => (
            <Button
              key={c}
              type="button"
              variant="outline"
              size="sm"
              data-testid={`plano-mass-cap-${c}`}
              onClick={() => onCapacidad(c)}
              className="rounded-xl h-8 text-xs"
            >
              {c}
            </Button>
          ))}
        </div>
        {!capacidadCustomOpen ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="plano-mass-cap-custom-open"
            onClick={() => setCapacidadCustomOpen(true)}
            className="rounded-xl h-8 text-xs"
          >
            Capacidad personalizada…
          </Button>
        ) : (
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              step={1}
              data-testid="plano-mass-cap-custom-input"
              value={capacidadCustomValor}
              onChange={(e) => setCapacidadCustomValor(e.target.value)}
              placeholder="Entero > 0"
              className="bg-background text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") onCapacidad(capacidadCustomValor);
              }}
            />
            <Button
              type="button"
              size="sm"
              data-testid="plano-mass-cap-custom-aplicar"
              onClick={() => onCapacidad(capacidadCustomValor)}
              className="rounded-xl"
            >
              Aplicar
            </Button>
          </div>
        )}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="plano-mass-eliminar"
        onClick={onEliminar}
        className="rounded-xl mt-1 text-destructive border-destructive/40 hover:bg-destructive/10"
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Eliminar {cantidad} {cantidad === 1 ? "mesa" : "mesas"}
      </Button>
    </div>
  );
}

/**
 * Panel "Configuración de mesas" · edición en tiempo real.
 * Todo cambio de tipo/capacidad/estado/observaciones/número se aplica
 * inmediatamente. NO existe botón "Aplicar cambios".
 */
function PanelConfiguracionMesas({
  mesas: seleccionadas,
  onAplicarUno,
  onAplicarSeleccion,
  onEliminar,
  soloDisponible = false,
}) {
  const multi = seleccionadas.length > 1;
  const tipoHomogeneo = new Set(seleccionadas.map((m) => m.tipo)).size === 1;
  const capHomogenea =
    new Set(seleccionadas.map((m) => m.capacidad)).size === 1;
  const estadoHomogeneo =
    new Set(seleccionadas.map((m) => m.estado)).size === 1;

  const [numeroDraft, setNumeroDraft] = useState(
    seleccionadas.length === 1 ? String(seleccionadas[0].numero) : "",
  );
  const [obsDraft, setObsDraft] = useState(
    seleccionadas.length === 1 ? seleccionadas[0].observaciones || "" : "",
  );
  const [capacidadCustomOpen, setCapacidadCustomOpen] = useState(false);
  const [capacidadCustomValor, setCapacidadCustomValor] = useState("");

  // Sincroniza los drafts locales cuando cambia la selección.
  useEffect(() => {
    setNumeroDraft(
      seleccionadas.length === 1 ? String(seleccionadas[0].numero) : "",
    );
    setObsDraft(
      seleccionadas.length === 1 ? seleccionadas[0].observaciones || "" : "",
    );
    setCapacidadCustomOpen(false);
    setCapacidadCustomValor("");
  }, [seleccionadas]);

  const aplicarNumero = () => {
    if (seleccionadas.length !== 1) return;
    const n = Number(numeroDraft);
    if (!Number.isFinite(n) || n < 1) {
      toast.error("El número debe ser un entero mayor a cero.");
      return;
    }
    onAplicarUno(seleccionadas[0].id, { numero: n });
  };

  const aplicarCapacidad = (val) => {
    const n = Number(val);
    if (!Number.isInteger(n) || n <= 0) {
      toast.error("La capacidad debe ser un entero mayor a cero.");
      return;
    }
    onAplicarSeleccion({ capacidad: n });
    setCapacidadCustomOpen(false);
    setCapacidadCustomValor("");
  };

  const capValue =
    capHomogenea && !multi
      ? String(seleccionadas[0].capacidad)
      : capHomogenea
        ? String(seleccionadas[0].capacidad)
        : "";
  const estadoValue = estadoHomogeneo ? seleccionadas[0].estado : "";
  const tipoValue = tipoHomogeneo ? seleccionadas[0].tipo : "";
  // Opciones de capacidad: estándar + la capacidad actual si es histórica
  // (p.ej. 20 de la Directiva) para no reemplazarla silenciosamente.
  const capOpciones = Array.from(
    new Set([...CAPACIDADES_MESA, ...(capValue ? [Number(capValue)] : [])]),
  ).sort((a, b) => a - b);
  // Orientación estructural (rotacion) desde los DATOS.
  const rotHomogeneo =
    new Set(seleccionadas.map((m) => (Number(m.rotacion) === 90 ? 90 : 0))).size === 1;
  const rotValue = rotHomogeneo
    ? String(Number(seleccionadas[0].rotacion) === 90 ? 90 : 0)
    : "";

  const Icon =
    seleccionadas.length === 1
      ? TIPO_MESA_ICON[seleccionadas[0].tipo] || Circle
      : CheckSquare;
  const tone =
    seleccionadas.length === 1
      ? ESTADO_MESA_TONE[seleccionadas[0].estado] || ESTADO_MESA_TONE.DISPONIBLE
      : ESTADO_MESA_TONE.DISPONIBLE;

  return (
    <div
      data-testid={EVENTO_PLANO.panelSelected}
      className="rounded-2xl border hairline bg-card p-5 flex flex-col gap-4"
    >
      <header className="flex items-start gap-3">
        <div
          className={cn(
            "w-10 h-10 rounded-xl grid place-items-center ring-1",
            tone.bg,
            tone.ring,
            tone.text,
          )}
        >
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="overline text-muted-foreground">Configuración de mesas</p>
          <p
            data-testid={EVENTO_PLANO.detNumero}
            className="font-heading text-xl leading-tight"
          >
            {multi
              ? `${seleccionadas.length} mesas seleccionadas`
              : `Mesa ${seleccionadas[0].numero}`}
          </p>
        </div>
      </header>

      {!multi && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Número</Label>
          <Input
            type="number"
            min={1}
            step={1}
            value={numeroDraft}
            onChange={(e) => setNumeroDraft(e.target.value)}
            onBlur={aplicarNumero}
            onKeyDown={(e) => {
              if (e.key === "Enter") aplicarNumero();
            }}
            className="bg-background"
          />
          <p className="text-[10px] text-muted-foreground">
            Se aplica al salir del campo o al presionar Enter. Rechaza
            duplicados en el mismo salón.
          </p>
        </div>
      )}

      {/* Tipo · aplica en tiempo real */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          Tipo
          {multi && !tipoHomogeneo && (
            <span className="text-muted-foreground ml-2">(distintos)</span>
          )}
        </Label>
        <Select
          value={tipoValue}
          onValueChange={(v) => onAplicarSeleccion({ tipo: v })}
        >
          <SelectTrigger
            data-testid={multi ? EVENTO_PLANO.bulkTipo : EVENTO_PLANO.detTipo}
            className="bg-background"
          >
            <SelectValue placeholder={multi && !tipoHomogeneo ? "Elegir para todas" : "Selecciona"} />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TIPO_MESA).map(([k, v]) => (
              <SelectItem key={k} value={v}>
                {TIPO_MESA_LABEL[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orientación (rotacion) · define vertical/horizontal desde los datos */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          Orientación
          {multi && !rotHomogeneo && (
            <span className="text-muted-foreground ml-2">(distintas)</span>
          )}
        </Label>
        <Select
          value={rotValue}
          onValueChange={(v) => onAplicarSeleccion({ rotacion: Number(v) })}
        >
          <SelectTrigger
            data-testid={EVENTO_PLANO.detOrientacion}
            className="bg-background"
          >
            <SelectValue placeholder={multi && !rotHomogeneo ? "Elegir para todas" : "Selecciona"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Horizontal</SelectItem>
            <SelectItem value="90">Vertical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Capacidad · aplica en tiempo real + custom */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          Capacidad
          {multi && !capHomogenea && (
            <span className="text-muted-foreground ml-2">(distintas)</span>
          )}
        </Label>
        <Select
          value={capacidadCustomOpen ? "__custom__" : capValue}
          onValueChange={(v) => {
            if (v === "__custom__") {
              setCapacidadCustomOpen(true);
              return;
            }
            setCapacidadCustomOpen(false);
            aplicarCapacidad(v);
          }}
        >
          <SelectTrigger
            data-testid={multi ? EVENTO_PLANO.bulkCapacidad : EVENTO_PLANO.detCapacidad}
            className="bg-background"
          >
            <SelectValue placeholder={multi && !capHomogenea ? "Elegir para todas" : "Selecciona"} />
          </SelectTrigger>
          <SelectContent>
            {capOpciones.map((c) => (
              <SelectItem key={c} value={String(c)}>
                {c} personas
              </SelectItem>
            ))}
            <SelectItem value="__custom__">Personalizada…</SelectItem>
          </SelectContent>
        </Select>
        {capacidadCustomOpen && (
          <div className="flex gap-2 mt-1">
            <Input
              type="number"
              min={1}
              step={1}
              data-testid="plano-det-capacidad-custom"
              value={capacidadCustomValor}
              onChange={(e) => setCapacidadCustomValor(e.target.value)}
              placeholder="Entero > 0"
              className="bg-background"
              onKeyDown={(e) => {
                if (e.key === "Enter") aplicarCapacidad(capacidadCustomValor);
              }}
            />
            <Button
              type="button"
              size="sm"
              data-testid="plano-det-capacidad-custom-aplicar"
              onClick={() => aplicarCapacidad(capacidadCustomValor)}
              className="rounded-xl"
            >
              Aplicar
            </Button>
          </div>
        )}
      </div>

      {/* Estado · aplica en tiempo real */}
      {!soloDisponible && (
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">Estado</Label>
        <Select
          value={estadoValue}
          onValueChange={(v) => onAplicarSeleccion({ estado: v })}
        >
          <SelectTrigger
            data-testid={multi ? EVENTO_PLANO.bulkEstado : EVENTO_PLANO.detEstado}
            className="bg-background"
          >
            <SelectValue placeholder={multi && !estadoHomogeneo ? "Elegir para todas" : "Selecciona"} />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ESTADO_MESA).map(([k, v]) => (
              <SelectItem key={k} value={v}>
                {ESTADO_MESA_LABEL[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}

      {/* Observaciones · aplica al perder foco (para no golpear la BD en
          cada tecla) — respeta el criterio 'tiempo real' desde el punto
          de vista del usuario. */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">Observaciones internas</Label>
        <Textarea
          data-testid={multi ? EVENTO_PLANO.bulkObservaciones : EVENTO_PLANO.detObservaciones}
          value={obsDraft}
          onChange={(e) => setObsDraft(e.target.value)}
          onBlur={() => onAplicarSeleccion({ observaciones: obsDraft })}
          rows={3}
          placeholder={
            multi
              ? "Se aplicará a todas las mesas seleccionadas al salir."
              : "Notas visibles sólo para el equipo del Casino."
          }
          className="bg-background resize-none"
        />
      </div>

      {/* Eliminar mesa · botón visible y evidente */}
      <div className="pt-2 border-t hairline">
        <Button
          type="button"
          variant="outline"
          data-testid={EVENTO_PLANO.btnEliminar}
          onClick={onEliminar}
          className="rounded-xl w-full text-destructive border-destructive/40 hover:bg-destructive/10"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {multi ? `Eliminar ${seleccionadas.length} mesas` : "Eliminar mesa"}
        </Button>
      </div>
    </div>
  );
}
